import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { submitSignupForm } from "../../api/signupAPI";
import {
  validateEmail,
  validateFirstName,
  validateLastName,
  validatePassword,
  validatePasswordMatch,
} from "../../utils/validationUtils";
import { routes } from "../../routes/routes";
import { getOrganizationId, setOrganizationId } from "../../utils/orgIdUtils";
import MaltoraLogo from "../landing/MaltoraLogo";

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: "46px",
  border: "1.5px solid rgba(15,23,42,0.14)",
  borderRadius: "8px",
  padding: "0 14px",
  fontFamily: "var(--font-ui, DM Sans, system-ui, sans-serif)",
  fontSize: "14px",
  color: "#0F172A",
  background: "#F8F9FA",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
};
const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "13px",
  fontWeight: "600",
  color: "#0F172A",
  marginBottom: "6px",
};
const errStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "#ef4444",
  marginTop: "4px",
  display: "block",
};

const SignUpScreen: React.FC = () => {
  const navigate = useNavigate();
  const orgId = getOrganizationId();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    companyName: "",
    password: "",
    confirmPassword: "",
    terms: "",
  });
  const [agreed, setAgreed] = useState(false);

  const isFormValid =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    email.trim() !== "" &&
    companyName.trim() !== "" &&
    password !== "" &&
    confirmPassword !== "" &&
    password === confirmPassword &&
    agreed;

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const firstNameError = validateFirstName(firstName);
    const lastNameError = validateLastName(lastName);
    const emailError = validateEmail(email);
    const companyNameError = companyName.trim()
      ? ""
      : "Please enter your company name.";
    const passwordRules = validatePassword(password);
    const passwordError = passwordRules.every((r) => r.isValid)
      ? ""
      : "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.";
    const confirmPasswordError = validatePasswordMatch(
      password,
      confirmPassword
    );

    const termsError = !agreed
      ? "You must agree to the Terms and Privacy Policy"
      : "";
    setErrors({
      firstName: firstNameError,
      lastName: lastNameError,
      email: emailError,
      companyName: companyNameError,
      password: passwordError,
      confirmPassword: confirmPasswordError,
      terms: termsError,
    });

    if (
      firstNameError ||
      lastNameError ||
      emailError ||
      companyNameError ||
      passwordError ||
      confirmPasswordError ||
      termsError
    ) {
      return;
    }

    try {
      const userData = {
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        email,
        companyName: companyName.trim(),
        password,
        signup_type: "direct",
      };
      console.log("Submitting signup form with orgId:", orgId, "and data:", {
        ...userData,
        password: "***",
      });
      const response = await submitSignupForm(userData);
      if (response?.data?.organizationId) {
        setOrganizationId(response.data.organizationId);
      }
      navigate(`/${routes.public.signUpConfirm}`);
    } catch (error: any) {
      console.error("Signup failed:", error);
      if (error?.response?.data?.message === "Email already registered") {
        setErrors((prev) => ({
          ...prev,
          email: "The entered Email-ID already exists.",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          email: "Something went wrong. Please try again.",
        }));
      }
    }
  };

  const focusTeal = (ev: React.FocusEvent<HTMLInputElement>) =>
    (ev.target.style.borderColor = "#1F5F5B");
  const blurReset = (
    ev: React.FocusEvent<HTMLInputElement>,
    hasError: boolean
  ) =>
    (ev.target.style.borderColor = hasError
      ? "#ef4444"
      : "rgba(15,23,42,0.14)");

  return (
    <>
      <style>{`
        input[type="password"]::-ms-reveal,
        input[type="password"]::-ms-clear {
          display: none;
        }
        input[type="password"]::-webkit-credentials-auto-fill-button {
          display: none;
        }
      `}</style>
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          fontFamily: "var(--font-ui, DM Sans, system-ui, sans-serif)",
        }}
      >
        {/* LEFT */}
        <div
          style={{
            width: "380px",
            flexShrink: 0,
            background: "linear-gradient(160deg,#0F2D2B 0%,#1F5F5B 100%)",
            display: "flex",
            flexDirection: "column",
            padding: "44px 40px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-60px",
              right: "-60px",
              width: "250px",
              height: "250px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "-80px",
              left: "-40px",
              width: "200px",
              height: "200px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.03)",
              pointerEvents: "none",
            }}
          />
          <Link
            to="/"
            style={{
              display: "block",
              width: "100px",
              marginBottom: "44px",
              flexShrink: 0,
            }}
          >
            <MaltoraLogo fill="#fff" />
          </Link>
          <span
            style={{
              display: "block",
              fontSize: "10.5px",
              fontWeight: "700",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#E86A0D",
              marginBottom: "12px",
            }}
          >
            Start for free
          </span>
          <h2
            style={{
              fontFamily:
                "var(--font-display, Satoshi, DM Sans, system-ui, sans-serif)",
              fontSize: "22px",
              fontWeight: "700",
              color: "#fff",
              lineHeight: "1.3",
              letterSpacing: "-0.02em",
              marginBottom: "12px",
            }}
          >
            Run your brewery.
            <br />
            <em
              style={{
                fontStyle: "italic",
                color: "rgba(255,255,255,0.6)",
                fontWeight: "700",
              }}
            >
              Finally, like a system.
            </em>
          </h2>
          <p
            style={{
              fontSize: "13.5px",
              color: "rgba(255,255,255,0.55)",
              lineHeight: "1.6",
              marginBottom: "28px",
            }}
          >
            One platform for orders, inventory, payments, and every stakeholder
            in your supply chain.
          </p>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "0 0 32px",
              display: "flex",
              flexDirection: "column",
              gap: "11px",
            }}
          >
            {[
              "Digital ordering for buyers and sellers",
              "Live inventory across all warehouses",
              "Payment tracking and credit management",
              "Sales rep dashboards and performance visibility",
            ].map((f) => (
              <li
                key={f}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  fontSize: "13.5px",
                  color: "rgba(255,255,255,0.8)",
                  lineHeight: "1.45",
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(147,197,253,0.9)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0, marginTop: "2px" }}
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {f}
              </li>
            ))}
          </ul>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginTop: "auto",
            }}
          >
            <div style={{ display: "flex" }}>
              {["#1F5F5B", "#5F8F3A", "#E86A0D", "#0B4740"].map((c, i) => (
                <div
                  key={i}
                  style={{
                    width: "26px",
                    height: "26px",
                    borderRadius: "50%",
                    background: c,
                    border: "2px solid rgba(255,255,255,0.2)",
                    marginLeft: i ? "-8px" : 0,
                  }}
                />
              ))}
            </div>
            <span
              style={{ fontSize: "12.5px", color: "rgba(255,255,255,0.65)" }}
            >
              500+ outlets managed
            </span>
          </div>
        </div>

        {/* RIGHT */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            background: "#fff",
            padding: "16px 24px 48px",
            overflowY: "auto",
          }}
        >
          <div style={{ width: "100%", maxWidth: "420px" }}>
            <h1
              style={{
                fontFamily:
                  "var(--font-display, Satoshi, DM Sans, system-ui, sans-serif)",
                fontSize: "28px",
                fontWeight: "700",
                color: "#0F172A",
                marginBottom: "6px",
                letterSpacing: "-0.02em",
              }}
            >
              Create your account
            </h1>
            <p
              style={{
                fontSize: "14px",
                color: "#64748B",
                marginBottom: "28px",
              }}
            >
              Already have an account?{" "}
              <Link
                to={`/${routes.public.signIn}`}
                state={{ fromApp: true }}
                style={{
                  color: "#1F5F5B",
                  fontSize: "14px",
                  fontWeight: "600",
                  textDecoration: "none",
                }}
              >
                Sign in
              </Link>
            </p>
            <form onSubmit={handleSignUp} noValidate>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginBottom: "18px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <label htmlFor="firstName" style={labelStyle}>
                    First name <span style={{ color: "#E86A0D" }}>*</span>
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    placeholder="Rajesh"
                    style={{
                      ...inputStyle,
                      borderColor: errors.firstName
                        ? "#ef4444"
                        : "rgba(15,23,42,0.14)",
                    }}
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      setErrors((prev) => ({ ...prev, firstName: "" }));
                    }}
                    onFocus={focusTeal}
                    onBlur={(ev) => blurReset(ev, !!errors.firstName)}
                  />
                  {errors.firstName && (
                    <span style={errStyle}>{errors.firstName}</span>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="lastName" style={labelStyle}>
                    Last name <span style={{ color: "#E86A0D" }}>*</span>
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    placeholder="Menon"
                    style={{
                      ...inputStyle,
                      borderColor: errors.lastName
                        ? "#ef4444"
                        : "rgba(15,23,42,0.14)",
                    }}
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      setErrors((prev) => ({ ...prev, lastName: "" }));
                    }}
                    onFocus={focusTeal}
                    onBlur={(ev) => blurReset(ev, !!errors.lastName)}
                  />
                  {errors.lastName && (
                    <span style={errStyle}>{errors.lastName}</span>
                  )}
                </div>
              </div>
              <div style={{ marginBottom: "18px" }}>
                <label htmlFor="email" style={labelStyle}>
                  Work email <span style={{ color: "#E86A0D" }}>*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="rajesh@yourcompany.com"
                  style={{
                    ...inputStyle,
                    borderColor: errors.email
                      ? "#ef4444"
                      : "rgba(15,23,42,0.14)",
                  }}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors((prev) => ({ ...prev, email: "" }));
                  }}
                  onFocus={focusTeal}
                  onBlur={(ev) => blurReset(ev, !!errors.email)}
                />
                {errors.email && <span style={errStyle}>{errors.email}</span>}
              </div>
              <div style={{ marginBottom: "18px" }}>
                <label htmlFor="companyName" style={labelStyle}>
                  Company name <span style={{ color: "#E86A0D" }}>*</span>
                </label>
                <input
                  id="companyName"
                  type="text"
                  placeholder="Your company name"
                  style={{
                    ...inputStyle,
                    borderColor: errors.companyName
                      ? "#ef4444"
                      : "rgba(15,23,42,0.14)",
                  }}
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    setErrors((prev) => ({ ...prev, companyName: "" }));
                  }}
                  onFocus={focusTeal}
                  onBlur={(ev) => blurReset(ev, !!errors.companyName)}
                />
                {errors.companyName && (
                  <span style={errStyle}>{errors.companyName}</span>
                )}
              </div>
              <div style={{ marginBottom: "18px" }}>
                <label htmlFor="password" style={labelStyle}>
                  Password <span style={{ color: "#E86A0D" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Min. 8 characters"
                    style={{
                      ...inputStyle,
                      paddingRight: "44px",
                      borderColor: errors.password
                        ? "#ef4444"
                        : "rgba(15,23,42,0.14)",
                    }}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrors((prev) => ({ ...prev, password: "" }));
                    }}
                    onFocus={focusTeal}
                    onBlur={(ev) => blurReset(ev, !!errors.password)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: "12px",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      color: "#64748B",
                      display: "flex",
                      alignItems: "center",
                      zIndex: 10,
                    }}
                  >
                    {showPassword ? (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <span style={errStyle}>{errors.password}</span>
                )}
              </div>
              <div style={{ marginBottom: "18px" }}>
                <label htmlFor="confirmPassword" style={labelStyle}>
                  Confirm password <span style={{ color: "#E86A0D" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Repeat password"
                    style={{
                      ...inputStyle,
                      paddingRight: "44px",
                      borderColor: errors.confirmPassword
                        ? "#ef4444"
                        : "rgba(15,23,42,0.14)",
                    }}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      const matchError = validatePasswordMatch(
                        password,
                        e.target.value
                      );
                      setErrors((prev) => ({
                        ...prev,
                        confirmPassword: matchError,
                      }));
                    }}
                    onFocus={focusTeal}
                    onBlur={(ev) => blurReset(ev, !!errors.confirmPassword)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    aria-label={
                      showConfirmPassword ? "Hide password" : "Show password"
                    }
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: "12px",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      color: "#64748B",
                      display: "flex",
                      alignItems: "center",
                      zIndex: 10,
                    }}
                  >
                    {showConfirmPassword ? (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span style={errStyle}>{errors.confirmPassword}</span>
                )}
              </div>
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  <input
                    type="checkbox"
                    id="terms"
                    checked={agreed}
                    onChange={(e) => {
                      setAgreed(e.target.checked);
                      setErrors((prev) => ({ ...prev, terms: "" }));
                    }}
                    style={{
                      marginTop: "3px",
                      accentColor: "#1F5F5B",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  />
                  <label
                    htmlFor="terms"
                    style={{
                      fontSize: "13px",
                      color: "#64748B",
                      lineHeight: "1.5",
                      cursor: "pointer",
                    }}
                  >
                    I agree to the{" "}
                    <Link
                      to="/terms"
                      state={{ fromApp: true }}
                      style={{
                        color: "#1F5F5B",
                        fontSize: "13px",
                        fontWeight: "600",
                        textDecoration: "none",
                      }}
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy"
                      state={{ fromApp: true }}
                      style={{
                        color: "#1F5F5B",
                        fontSize: "13px",
                        fontWeight: "600",
                        textDecoration: "none",
                      }}
                    >
                      Privacy Policy
                    </Link>
                  </label>
                </div>
                {errors.terms && (
                  <span style={{ ...errStyle, marginTop: "6px" }}>
                    {errors.terms}
                  </span>
                )}
              </div>
              <button
                type="submit"
                disabled={!isFormValid}
                style={{
                  width: "100%",
                  height: "48px",
                  background: isFormValid ? "#E86A0D" : "#E86A0D80",
                  color: "#fff",
                  fontFamily: "var(--font-ui, DM Sans, system-ui, sans-serif)",
                  fontSize: "15px",
                  fontWeight: "700",
                  border: "none",
                  borderRadius: "10px",
                  cursor: isFormValid ? "pointer" : "not-allowed",
                  letterSpacing: "0.01em",
                  transition: "background 0.15s",
                  marginBottom: "12px",
                  opacity: isFormValid ? 1 : 0.6,
                }}
                onMouseEnter={(e) => {
                  if (isFormValid) e.currentTarget.style.background = "#d45e0b";
                }}
                onMouseLeave={(e) => {
                  if (isFormValid) e.currentTarget.style.background = "#E86A0D";
                }}
              >
                Create Account
              </button>
              <p
                style={{
                  fontSize: "12px",
                  color: "#64748B",
                  textAlign: "center",
                }}
              >
                Free 14-day trial. No credit card required.
              </p>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
export default SignUpScreen;
